import React, { useEffect, useState } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Stack,
  Avatar,
  Alert,
} from '@mui/material';
import {
  PeopleAlt,
  CheckCircle,
  BlockOutlined,
  FlagOutlined,
} from '@mui/icons-material';
import { getAdminDashboardSummary } from '../store/actions';
import { useDispatch } from 'react-redux';
const StatCard = ({ title, value, subtitle, color = "info", icon: Icon }) => (
  <Card
    sx={{
      borderRadius: 3,
      boxShadow: 3,
      height: 100,
      transition: '0.3s',
      '&:hover': { boxShadow: 6, transform: 'translateY(-3px)' },
    }}
    className={`stat-card ${color}`}
  >
    <Stack direction="row" alignItems="center" spacing={3} sx={{ padding: 2 }}>
      <Avatar
        sx={{
          bgcolor: `${color}.main`,
          width: 56,
          height: 56,
          boxShadow: 2,
        }}
      >
        <Icon fontSize="large" />
      </Avatar>

      <Box>
        <Typography variant="subtitle2" color="text.secondary">
          {title}
        </Typography>
        <Typography variant="h5" fontWeight="bold">
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Box>
    </Stack>
  </Card>
);

const UserEngagementDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [error, setError] = useState(null);
  const dispatch = useDispatch();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    dispatch(getAdminDashboardSummary(
      (data) => { setDashboardData(data); setError(null); },
      (err) => setError(err.message || 'Failed to fetch dashboard data'
      )))
  };

  if (error) {
    return (
      <Alert severity="error" onClose={fetchDashboardData} sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  const userData = dashboardData?.users || { total: 0, active: 0, blocked: 0 };
  const challengeData = dashboardData?.challenges || { total: 0, active: 0, completed: 0 };
  const postData = dashboardData?.posts || { total: 0, flagged: 0, blocked: 0 };
  const moodLogsCount = dashboardData?.moodLogs || 0;
  const notificationData = dashboardData?.notifications || { total: 0, unread: 0 };

  return (
    <Box>
      {/* <Typography variant="h5" gutterBottom fontWeight="bold">
        User Engagement Metrics
      </Typography> */}

      <Grid container spacing={2}>
        {/* Users Stats */}
        <Grid item xs={12} md={3}>
          <StatCard
            title="Total Users"
            value={userData.total}
            color="info"
            icon={PeopleAlt}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Active Users"
            value={userData.active}
            color="success"
            icon={CheckCircle}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Blocked Users"
            value={userData.blocked}
            color="error"
            icon={BlockOutlined}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Flagged Posts"
            value={postData.flagged}
            color="warning"
            icon={FlagOutlined}
          />
        </Grid>

        {/* Challenges & Content Stats */}
        <Grid item xs={12} md={8}>
          <Card
            sx={{
              borderRadius: 3,
              boxShadow: 3,
              height: '100%',
            }}
          >
            <CardContent>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                Content & Challenges Overview
              </Typography>
              <Stack spacing={2} sx={{ mt: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', pb: 1, borderBottom: '1px solid #eee' }}>
                  <Typography variant="body2" color="text.secondary">
                    Total Challenges
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {challengeData.total}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', pb: 1, borderBottom: '1px solid #eee' }}>
                  <Typography variant="body2" color="text.secondary">
                    Active Challenges
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {challengeData.active}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', pb: 1, borderBottom: '1px solid #eee' }}>
                  <Typography variant="body2" color="text.secondary">
                    Completed Challenges
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {challengeData.completed}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', pb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Total Posts
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {postData.total}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* User Activity */}
        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 3, boxShadow: 3, height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                User Activity
              </Typography>
              <Stack spacing={2} sx={{ mt: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', pb: 1, borderBottom: '1px solid #eee' }}>
                  <Typography variant="body2" color="text.secondary">
                    Mood Logs
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {moodLogsCount}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', pb: 1, borderBottom: '1px solid #eee' }}>
                  <Typography variant="body2" color="text.secondary">
                    Notifications
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {notificationData.total}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    Unread Notifications
                  </Typography>
                  <Typography variant="body2" fontWeight="bold" color="error">
                    {notificationData.unread}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default UserEngagementDashboard;
